/**
 * Маппинг `message_type` → иконка/emoji/подпись для UI Inbox.
 *
 * Контракт inbox §C.6, INB §7 (REQ-INB-006). Используется в карточках dropdown
 * (§C.8.2) и страницы `/inbox` (§C.8.3) для визуальной дифференциации типов
 * сообщений. Неизвестный тип → fallback (`IconBell`/📬/'Уведомление') — не падаем
 * (AC-INB-006.1): если enum расширится на бэке без правки фронта, карточка всё
 * равно рендерится с осмысленной иконкой.
 */

import {
  IconBell,
  IconCalendarEvent,
  IconChartBar,
  IconNews,
  IconPresentation,
  IconStarFilled,
  IconTicket,
  type TablerIcon,
} from '@tabler/icons-react';

import type { MessageType } from '@/lib/api/types';

export interface MessageTypeMeta {
  /** Иконка `@tabler` для карточки. */
  icon: TablerIcon;
  /** Emoji-вариант (для OS-уведомлений, где иконка-компонент не подходит). */
  emoji: string;
  /** Человекочитаемый ру-лейбл. */
  label: string;
}

/** Словарь известных типов (parity enum `MessageType`). */
const MESSAGE_TYPE_META: Record<MessageType, MessageTypeMeta> = {
  Helpdesk: { icon: IconTicket, emoji: '🎫', label: 'Поддержка' },
  CRM: { icon: IconChartBar, emoji: '📊', label: 'CRM' },
  'News Digest': { icon: IconNews, emoji: '📰', label: 'Новостная рассылка' },
  'News Highlight': {
    icon: IconStarFilled,
    emoji: '⭐',
    label: 'Главная новость',
  },
  Seminar: { icon: IconPresentation, emoji: '🎓', label: 'Семинар' },
  // add-event-registration Step 1 minimal (полный inbox UI — Step 2)
  'Event Registration': {
    icon: IconCalendarEvent,
    emoji: '📅',
    label: 'Мои мероприятия',
  },
};

/** Fallback для неизвестного типа (AC-INB-006.1 — не падаем). */
const FALLBACK_META: MessageTypeMeta = {
  icon: IconBell,
  emoji: '📬',
  label: 'Уведомление',
};

/**
 * Возвращает мета типа сообщения. Неизвестный тип → fallback (не бросает,
 * AC-INB-006.1). Если enum на бэке расширится без правки фронта — карточка
 * рендерится с fallback-иконкой.
 */
export function getMessageTypeMeta(type: string): MessageTypeMeta {
  return MESSAGE_TYPE_META[type as MessageType] ?? FALLBACK_META;
}
